import React, { useState, useRef, useEffect } from 'react';

/**
 * Tooltip Component
 * 
 * Displays helpful information on hover.
 * Supports multiple positions and customizable styling.
 * 
 * Usage:
 * <Tooltip text="This is helpful info">
 *   <button>Hover me</button>
 * </Tooltip>
 * 
 * Props:
 * - text: The tooltip content (required)
 * - position: 'top' | 'bottom' | 'left' | 'right' (default: 'bottom')
 * - delay: Show delay in milliseconds (default: 200)
 * - maxWidth: Max width in pixels (default: 400)
 * - children: The element to attach tooltip to
 */
const Tooltip = ({ 
  text, 
  position = 'bottom', 
  delay = 200, 
  maxWidth = 400,
  children 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [actualPosition, setActualPosition] = useState(position);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  const handleMouseEnter = () => {
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setIsVisible(false);
  };

  // Smart positioning logic
  useEffect(() => {
    if (isVisible && tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Get tooltip dimensions
      const tooltipRect = tooltip.getBoundingClientRect();
      
      let bestPosition = position;
      
      // Check if tooltip would be cut off and find best position
      const positions = ['bottom', 'top', 'right', 'left'];
      
      for (const pos of positions) {
        let wouldFit = true;
        
        switch (pos) {
          case 'bottom':
            if (containerRect.bottom + tooltipRect.height + 20 > viewportHeight) wouldFit = false;
            if (containerRect.left + tooltipRect.width / 2 > viewportWidth) wouldFit = false;
            if (containerRect.left - tooltipRect.width / 2 < 200) wouldFit = false; // Account for sidebar
            break;
          case 'top':
            if (containerRect.top - tooltipRect.height - 20 < 0) wouldFit = false;
            if (containerRect.left + tooltipRect.width / 2 > viewportWidth) wouldFit = false;
            if (containerRect.left - tooltipRect.width / 2 < 200) wouldFit = false;
            break;
          case 'right':
            if (containerRect.right + tooltipRect.width + 20 > viewportWidth) wouldFit = false;
            if (containerRect.top + tooltipRect.height / 2 > viewportHeight) wouldFit = false;
            if (containerRect.top - tooltipRect.height / 2 < 0) wouldFit = false;
            break;
          case 'left':
            if (containerRect.left - tooltipRect.width - 20 < 200) wouldFit = false; // Account for sidebar
            if (containerRect.top + tooltipRect.height / 2 > viewportHeight) wouldFit = false;
            if (containerRect.top - tooltipRect.height / 2 < 0) wouldFit = false;
            break;
          default:
            break;
        }
        
        if (wouldFit) {
          bestPosition = pos;
          break;
        }
      }
      
      setActualPosition(bestPosition);
    }
  }, [isVisible, position]);

  // Position-specific styles with sidebar awareness
  const getPositionStyles = (pos) => {
    const styles = {
      top: {
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%) translateY(-12px)',
        marginBottom: '4px'
      },
      bottom: {
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%) translateY(12px)',
        marginTop: '4px'
      },
      left: {
        right: '100%',
        top: '50%',
        transform: 'translateY(-50%) translateX(-12px)',
        marginRight: '4px'
      },
      right: {
        left: '100%',
        top: '50%',
        transform: 'translateY(-50%) translateX(12px)',
        marginLeft: '4px'
      }
    };
    return styles[pos] || styles.bottom;
  };

  // Arrow position styles
  const getArrowStyles = (pos) => {
    const styles = {
      top: {
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #1F2937'
      },
      bottom: {
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: '6px solid #1F2937'
      },
      left: {
        left: '100%',
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderLeft: '6px solid #1F2937'
      },
      right: {
        right: '100%',
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderRight: '6px solid #1F2937'
      }
    };
    return styles[pos] || styles.bottom;
  };

  if (!text) return children;

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed pointer-events-none"
          style={{
            zIndex: 999999,
            position: 'fixed',
            ...((() => {
              if (!containerRef.current) return { left: '50%', top: '50%' };
              
              const containerRect = containerRef.current.getBoundingClientRect();
              const tooltipWidth = tooltipRef.current ? tooltipRef.current.offsetWidth : maxWidth;
              const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 100;
              
              let left = containerRect.left + (containerRect.width / 2);
              let top = containerRect.top;
              let transform = '';
              
              switch (actualPosition) {
                case 'bottom':
                  top = containerRect.bottom + 12;
                  left = Math.min(
                    Math.max(left, tooltipWidth / 2 + 10), // Don't go past left edge
                    window.innerWidth - tooltipWidth / 2 - 10 // Don't go past right edge
                  );
                  transform = 'translateX(-50%)';
                  break;
                case 'top':
                  top = containerRect.top - tooltipHeight - 12;
                  left = Math.min(
                    Math.max(left, tooltipWidth / 2 + 10),
                    window.innerWidth - tooltipWidth / 2 - 10
                  );
                  transform = 'translateX(-50%)';
                  break;
                case 'left':
                  left = containerRect.left - tooltipWidth - 12;
                  top = containerRect.top + (containerRect.height / 2);
                  transform = 'translateY(-50%)';
                  break;
                case 'right':
                  left = containerRect.right + 12;
                  top = containerRect.top + (containerRect.height / 2);
                  transform = 'translateY(-50%)';
                  break;
                default:
                  transform = 'translateX(-50%)';
              }
              
              return { left: `${left}px`, top: `${top}px`, transform };
            })())
          }}
        >
          <div
            className="bg-gray-800 text-white text-sm px-4 py-3 rounded-lg shadow-2xl normal-case whitespace-normal leading-relaxed"
            style={{ 
              maxWidth: `${maxWidth}px`,
              minWidth: '200px',
              width: 'max-content',
              wordWrap: 'break-word'
            }}
            role="tooltip"
          >
            {text}
            {/* Arrow */}
            <div
              className="absolute"
              style={getArrowStyles(actualPosition)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;
